import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import cx from 'classnames';

import {
    Flex,
    Button,
    Spacer,
} from '@zillow/constellation';

import PostingActions from 'app/shared/actions/PostingActions';
import { RoutingSelectors } from 'app/shared/selectors';
import { StyledNoBorderButton, StyledNoWrapButton } from 'app/shared/StyledComponents/StyledComponents';

import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import { GA_EVENTS, GA_SUFFIX } from 'app/shared/constants/AnalyticsPaymentsModuleConstants';
import ConnectedModuleCardConstants from 'app/shared/constants/ConnectedModuleCardConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import EmptyPaymentsCard from './components/EmptyPaymentsCard';
import PaymentsMessageModule from './components/PaymentsMessageModule';
import PaymentCard from './components/PaymentCard';

import {
    StyledFlexPaymentCard,
    StyledConnectedModuleCard,
    StyledFluidFlex,
} from './StyledComponents';

import MESSAGE_MODULE_TYPES from './MESSAGE_MODULE_TYPES';

const NAVIGATION_STYLING = {
    TEXT_LINK: 'text_link',
    PRIMARY_BUTTON: 'primary_button',
    SECONDARY_BUTTON: 'secondary_button',
};

const {
    PAYMENT_TEXT_CTA_CLICK,
    MANAGE_TENANT_TERMS_CLICK,
    PAYMENT_CTA_CLICK,
} = GA_EVENTS;

const { TEXT_LINK } = GA_SUFFIX;

const ConnectedPayments = () => {
    const dispatch = useDispatch();

    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    const [paymentsSummary, setPaymentsSummary] = useState(null);
    const [showMessageModule, setShowMessageModule] = useState(true);

    useEffect(() => {
        // listingId is required
        if (!listingId) {
            return;
        }

        dispatch(PostingActions.property.getPaymentsSummary(listingId))
            .then((response) => {
                setPaymentsSummary(response);
            })
            .catch(() => {
                setPaymentsSummary(false);
            });
    }, [dispatch, listingId]);

    const isLoading = paymentsSummary === null;
    const isFailedRequest = paymentsSummary === false;

    if (isLoading) {
        return (
            <StyledConnectedModuleCard
                title="Payments"
                isLoading={isLoading}
                spinnerHeight={ConnectedModuleCardConstants.MANAGE_TENANTS_SPINNER_HEIGHT}
                spinnerSize="md"
            />
        );
    }

    if (isFailedRequest) {
        return (
            <StyledConnectedModuleCard title="Payments" />
        );
    }

    const closeMessageModule = (actionId) => {
        dispatch(PostingActions.property.markAcknowledgedLandlordMessage(actionId))
            .then(() => {
                setShowMessageModule(false);
            })
            .catch(() => {
                setShowMessageModule(false);
            });
    };

    const handleTopNavTextClick = (event) => {
        const action = `${stringHelper.startCaseRemoveSpace(event.target.textContent)}${DELIMITER}${TEXT_LINK}`;

        dispatch(analyticsUtils.track.GAEventV2({
            ...PAYMENT_TEXT_CTA_CLICK,
            action,
        }));
    };

    const handleBottomNavTextClick = () => {
        // event is not 100% dynamic
        // because we don't want to pass back the users name
        dispatch(analyticsUtils.track.GAEventV2(MANAGE_TENANT_TERMS_CLICK));
    };

    const handleBottomNavBtnClick = (event) => {
        const action = `${stringHelper.startCaseRemoveSpace(event.target.textContent)}`;

        dispatch(analyticsUtils.track.GAEventV2({
            ...PAYMENT_CTA_CLICK,
            action,
        }));
    };

    const renderTopNavigation = (navigation) => {
        if (navigation && navigation.topNavigation) {
            const { text, url, styling } = navigation.topNavigation;

            if (styling === NAVIGATION_STYLING.PRIMARY_BUTTON) {
                return (
                    <Button
                        as="a"
                        href={url}
                        buttonType="primary"
                    >
                        {text}
                    </Button>
                );
            }

            if (styling === NAVIGATION_STYLING.SECONDARY_BUTTON) {
                return (
                    <Button
                        as="a"
                        href={url}
                        buttonType="secondary"
                    >
                        {text}
                    </Button>
                );
            }

            return (
                <StyledNoBorderButton
                    forwardedAs="a"
                    href={url}
                    onClick={handleTopNavTextClick}
                >
                    {text}
                </StyledNoBorderButton>
            );
        }

        return null;
    };

    const renderBottomNavigation = (navigation) => {
        if (navigation && navigation.bottomNavigationList && navigation.bottomNavigationList.length) {
            return (
                <Flex
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mediaQuery={{
                        xs: {
                            flexDirection: 'column-reverse',
                        },
                        md: {
                            flexDirection: 'row',
                        },
                    }}
                >
                    { navigation.bottomNavigationList
                        .map(({ text, url, styling }) => {
                            const buttonProps = {
                                // StyledNoBorderButton needs forwardedAs to forward the `as` prop to the styled component
                                forwardedAs: 'a',
                                href: url,
                                fluid: true,
                            };

                            if (styling === NAVIGATION_STYLING.PRIMARY_BUTTON) {
                                return (
                                    <StyledNoWrapButton
                                        buttonType="primary"
                                        onClick={handleBottomNavBtnClick}
                                        {...buttonProps}
                                    >
                                        {text}
                                    </StyledNoWrapButton>
                                );
                            }

                            if (styling === NAVIGATION_STYLING.SECONDARY_BUTTON) {
                                return (
                                    <StyledNoWrapButton
                                        buttonType="secondary"
                                        onClick={handleBottomNavBtnClick}
                                        {...buttonProps}
                                    >
                                        {text}
                                    </StyledNoWrapButton>
                                );
                            }

                            return (
                                <StyledNoBorderButton
                                    onClick={handleBottomNavTextClick}
                                    {...buttonProps}
                                >
                                    {text}
                                </StyledNoBorderButton>
                            );
                        })
                        .map(navComponent => (<StyledFluidFlex>{navComponent}</StyledFluidFlex>))
                    }
                </Flex>
            );
        }

        return null;
    };

    const renderMessageModule = (messageModule) => {
        if (!messageModule || !showMessageModule) {
            return null;
        }

        return <PaymentsMessageModule {...messageModule} closeMessageModule={closeMessageModule} />;
    };

    const renderPaymentCards = (paymentCardList) => {
        if (!paymentCardList) {
            return [];
        }

        return paymentCardList.map(card => <PaymentCard {...card} />);
    };

    const createPaymentCardList = cards => cards
        .filter(card => Boolean(card))
        .map((card, i) => (
            <StyledFlexPaymentCard
                className={cx('PaymentCard', {
                    'PaymentCard-Even': i % 2 === 0,
                })}
                flex="0 50%"
            >
                {card}
            </StyledFlexPaymentCard>
        ));

    const {
        messageModule,
        navigation,
        paymentCardList,
        title,
    } = paymentsSummary;

    return (
        <StyledConnectedModuleCard
            title={title}
            ctaComponent={renderTopNavigation(navigation)}
        >
            <Flex display="flex" flexDirection="column" justifyContent="space-between" flex="1 0">
                <Flex
                    display="flex"
                    mediaQuery={{
                        xs: {
                            flexDirection: 'column',
                        },
                        md: {
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                        },
                    }}
                >
                    {
                        (messageModule && messageModule.type === MESSAGE_MODULE_TYPES.ONBOARDING) ? (
                            <Flex
                                className="PaymentCard"
                                flex="1 50%"
                            >
                                <EmptyPaymentsCard {...messageModule} />
                            </Flex>
                        ) :
                            createPaymentCardList([
                                renderMessageModule(messageModule),
                                ...renderPaymentCards(paymentCardList),
                            ])

                    }
                </Flex>
                { navigation && (
                    <Spacer paddingTop="xs">
                        {renderBottomNavigation(navigation)}
                    </Spacer>
                )}
            </Flex>
        </StyledConnectedModuleCard>
    );
};

export default ConnectedPayments;
